import { useEffect, useState, useCallback } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatApiError, downloadInvoicePdf, downloadPdf, fileUrl } from "@/lib/api";
import OrderMiniMap from "@/components/OrderMiniMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, Package, Users, Tag, Truck, FileText, CreditCard, Settings as SettingsIcon,
  Plus, Trash2, Check, AlertTriangle, Download, Pencil, Repeat, Pause, Play, Mail, MessageSquare, QrCode, Scale, History, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const money = (n) => "£" + Number(n || 0).toFixed(2);
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: Package },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "clients", label: "Clients", icon: Users },
  { key: "pricing", label: "Price list", icon: Tag },
  { key: "drivers", label: "Drivers & jobs", icon: Truck },
  { key: "invoices", label: "Invoices", icon: FileText },
  { key: "subscription", label: "Subscription", icon: CreditCard },
  { key: "account", label: "Settings", icon: SettingsIcon },
];
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700", collected: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-700", ready: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-indigo-100 text-indigo-700", completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};


function Clients() {
  const [clients, setClients] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", address: "", notes: "", create_login: true });
  const load = useCallback(() => api.get("/business/clients").then((r) => { setClients(r.data); setLoaded(true); }), []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => setEditing({ id: c.id, company_name: c.company_name || "", contact_name: c.contact_name || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "" });
  const saveEdit = async () => {
    if (!editing.company_name || !editing.contact_name) return toast.error("Company and contact name are required");
    try {
      await api.put(`/business/clients/${editing.id}`, {
        company_name: editing.company_name, contact_name: editing.contact_name,
        phone: editing.phone, address: editing.address, notes: editing.notes });
      toast.success("Client updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const create = async () => {
    if (!form.company_name || !form.contact_name || !form.email) return toast.error("Company, contact and email are required");
    try {
      const { data } = await api.post("/business/clients", { ...form, origin_url: window.location.origin });
      toast.success(data.set_password_email_sent ? "Client added — a login link was emailed" : "Client added");
      setOpen(false); setForm({ company_name: "", contact_name: "", email: "", phone: "", address: "", notes: "", create_login: true }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this client and its login?")) return; await api.delete(`/business/clients/${id}`); load(); };
  const emailStatement = async (id) => {
    try { await api.post(`/business/clients/${id}/statement/email`); toast.success("Statement emailed to client"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Clients</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-client-btn"><Plus className="mr-1 h-4 w-4" /> Add client</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add commercial client</DialogTitle><DialogDescription>Add a hotel, bar or B&B and optionally send them a portal login.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Company / venue name</Label><Input value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} data-testid="client-company" placeholder="The Grand Hotel" /></div>
              <div className="space-y-1.5"><Label>Contact name</Label><Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="client-contact" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="client-email" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="client-phone" /></div>
              </div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="client-address" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.create_login} onChange={(e) => setForm((f) => ({ ...f, create_login: e.target.checked }))} data-testid="client-create-login" /> Send this client a portal login (set-password email)</label>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="client-save-btn">Add client</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {clients.map((c) => (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-heading font-bold text-slate-900">{c.company_name}</p>
                <p className="text-sm text-slate-500">{c.contact_name}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => openEdit(c)} aria-label={`Edit ${c.company_name}`} title="Edit client" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-brand" data-testid={`client-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(c.id)} aria-label={`Delete ${c.company_name}`} title="Delete client" className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-500" data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-2 text-xs text-slate-400">{c.email}</p>
            <div className="mt-3 flex gap-4 text-sm">
              <span>{c.order_count} orders</span>
              <span className="text-amber-600">{money(c.outstanding)} due</span>
            </div>
            {c.user_id ? <Badge className="mt-3 border-0 bg-teal-100 text-teal-700">Has portal login</Badge> : <Badge className="mt-3 border-0 bg-slate-100 text-slate-600">No login</Badge>}
            <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
              <Button size="sm" variant="outline" onClick={() => downloadPdf(`/business/clients/${c.id}/statement`, `statement-${c.company_name}.pdf`).catch(() => toast.error("Could not download"))} data-testid={`client-statement-${c.id}`}><Download className="mr-1 h-3 w-3" /> Statement</Button>
              <Button size="sm" variant="outline" onClick={() => emailStatement(c.id)} data-testid={`client-email-statement-${c.id}`}><Mail className="mr-1 h-3 w-3" /> Email</Button>
            </div>
          </div>
        ))}
        {!clients.length && <p className="text-sm text-slate-400">{loaded ? "No clients yet. Add your first commercial client." : "Loading…"}</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="client-edit-dialog">
          <DialogHeader><DialogTitle>Edit client</DialogTitle><DialogDescription>Update this client&rsquo;s details. Their login email stays the same.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Company / venue name</Label><Input value={editing.company_name} onChange={(e) => setEditing((f) => ({ ...f, company_name: e.target.value }))} data-testid="client-edit-company" /></div>
              <div className="space-y-1.5"><Label>Contact name</Label><Input value={editing.contact_name} onChange={(e) => setEditing((f) => ({ ...f, contact_name: e.target.value }))} data-testid="client-edit-contact" /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={editing.phone} onChange={(e) => setEditing((f) => ({ ...f, phone: e.target.value }))} data-testid="client-edit-phone" /></div>
              <div className="space-y-1.5"><Label>Address</Label><Input value={editing.address} onChange={(e) => setEditing((f) => ({ ...f, address: e.target.value }))} data-testid="client-edit-address" /></div>
              <div className="space-y-1.5"><Label>Notes</Label><Textarea value={editing.notes} onChange={(e) => setEditing((f) => ({ ...f, notes: e.target.value }))} data-testid="client-edit-notes" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="client-edit-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------- Pricing -------------------------------- */

export default Clients;
